<template>
  <div
    class="offer-preview"
    :class="{ 'offer-preview-hover': asHover }"
  >
    <!-- Form preview -->
    <div v-if="isForm && formFields && formFields.length > 0">
      <div class="space-y-3">
        <!-- Form title -->
        <div
          v-if="offer.parts?.title"
          class="text-base font-semibold text-gray-900 dark:text-white"
        >
          <span
            v-html="
              highlightChanges(
                process(offer.parts.title),
                variantDiff?.parts?.title
              )
            "
          />
        </div>

        <!-- Form description -->
        <div
          v-if="offer.parts?.description"
          class="text-sm text-gray-700 opacity-80 dark:text-gray-200 [&_p]:my-2"
        >
          <span
            v-html="
              highlightChanges(
                process(offer.parts.description),
                variantDiff?.parts?.description
              )
            "
          />
        </div>

        <!-- Form fields -->
        <div class="mt-3 space-y-2">
          <div
            v-for="field in formFields"
            :key="field.id"
            class="offer-preview-field"
          >
            <label
              v-if="field.displayType !== 'submit'"
              class="text-xs font-medium opacity-70 dark:text-white/70"
            >
              {{ field.label }}
            </label>
            <div
              v-if="field.displayType !== 'submit'"
              class="offer-preview-input"
            >
              <input
                v-if="
                  field.displayType === 'text' ||
                    field.displayType === 'email' ||
                    field.type === 'text' ||
                    field.type === 'email'
                "
                :type="field.displayType || field.type || 'text'"
                :placeholder="field.placeholder || ''"
                disabled
                class="w-full rounded border px-2 py-1 text-sm opacity-50"
              >
              <textarea
                v-else-if="
                  field.displayType === 'textarea' || field.type === 'textarea'
                "
                :placeholder="field.placeholder || ''"
                disabled
                rows="2"
                class="w-full rounded border px-2 py-1 text-sm opacity-50"
              />
              <select
                v-else-if="
                  field.displayType === 'select' || field.type === 'select'
                "
                disabled
                class="w-full rounded border px-2 py-1 text-sm opacity-50"
              >
                <option>{{ field.placeholder || 'Select an option' }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Submit button -->
        <div
          v-if="offer.parts?.buttonText"
          class="mt-3"
        >
          <button
            disabled
            class="cursor-not-allowed rounded border border-blue-500/20 bg-gradient-to-br from-blue-500 to-blue-600 px-4 py-2 text-sm text-white opacity-75 shadow-sm dark:border-blue-600/20 dark:from-blue-600 dark:to-blue-700"
          >
            <span
              v-html="
                highlightChanges(
                  process(offer.parts.buttonText),
                  variantDiff?.parts?.buttonText
                )
              "
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Offer (CTA) preview -->
    <div
      v-else
      class="space-y-3"
    >
      <!-- Offer image -->
      <div
        v-if="offer.parts?.imageUrl"
        class="offer-preview-image"
      >
        <img
          :src="getExternalUrl(offer.parts.imageUrl)"
          :alt="offer.parts?.title || 'Offer image'"
          class="h-32 w-full rounded object-cover"
          :class="{ 'ring-2 ring-amber-400': variantDiff?.parts?.imageUrl }"
        >
      </div>

      <!-- Offer title -->
      <div
        v-if="offer.parts?.title"
        class="text-base font-semibold text-gray-900 dark:text-white"
      >
        <span
          v-html="
            highlightChanges(
              process(offer.parts.title),
              variantDiff?.parts?.title
            )
          "
        />
      </div>

      <!-- Offer description -->
      <div
        v-if="offer.parts?.description"
        class="text-sm text-gray-700 opacity-80 dark:text-gray-200 [&_p]:my-2"
      >
        <span
          v-html="
            highlightChanges(
              process(offer.parts.description),
              variantDiff?.parts?.description
            )
          "
        />
      </div>

      <!-- CTA button -->
      <div
        v-if="offer.parts?.buttonText"
        class="mt-3"
      >
        <a
          href="#"
          class="inline-block rounded border border-blue-500/20 bg-gradient-to-br from-blue-500 to-blue-600 px-4 py-2 text-sm text-white no-underline shadow-sm hover:from-blue-600 hover:to-blue-700 dark:border-blue-600/20 dark:from-blue-600 dark:to-blue-700 dark:hover:from-blue-700 dark:hover:to-blue-800"
          @click.prevent
        >
          <span
            v-html="
              highlightChanges(
                process(offer.parts.buttonText),
                variantDiff?.parts?.buttonText
              )
            "
          />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  offer: any
  isForm: boolean
  formFields?: any[]
  getExternalUrl: (url: string) => string
  process: (text: string) => string
  asHover?: boolean
  variantDiff?: any
}

const props = defineProps<Props>()

// Highlight changes in variant text
const highlightChanges = (text: string, hasChange: boolean) => {
  if (!hasChange) return text
  return `<span class="underline decoration-amber-400 decoration-2">${text}</span>`
}
</script>

